import { parseAgentText, type RichSpan } from "@/utils/agentMarkdown";
import { aiAgentStepLabel, type AiAgentStep } from "@/services/aiAgentApi";

/*
 * Текст ответа ассистента с разметкой агента: жирное, списки, заголовки,
 * таблицы.
 *
 * Раньше ответ шёл в `whitespace-pre-wrap` как есть, и звёздочки `**` стояли
 * прямо на экране. Разбор — `utils/agentMarkdown`: он отдаёт данные, а не HTML,
 * поэтому разметка из чужого сервиса в DOM не попадает. Тот же разбор у
 * заготовок — они тоже написаны Markdown.
 */

/*
 * ТАБЛИЦЫ — ОТДЕЛЬНЫМ БЛОКОМ. Агент сводит органы и районы в таблицу
 * (`| Орган | Решений после срока |`), и построчный разбор показал бы её
 * сырыми строками с палками. Строка таблицы — та, что начинается и кончается
 * `|`; разделитель `|---|` не рисуется. Пока ответ набирается, последняя
 * строка таблицы может быть оборвана — она просто дорисуется следующим кадром.
 */
type Block = { kind: "text"; text: string } | { kind: "table"; rows: string[][] };

const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_SEPARATOR = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

function splitBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  let buffer: string[] = [];
  let rows: string[][] = [];
  const flushText = () => {
    if (buffer.length) blocks.push({ kind: "text", text: buffer.join("\n") });
    buffer = [];
  };
  const flushTable = () => {
    if (rows.length) blocks.push({ kind: "table", rows });
    rows = [];
  };
  for (const line of text.split("\n")) {
    if (TABLE_ROW.test(line)) {
      flushText();
      if (!TABLE_SEPARATOR.test(line)) {
        rows.push(line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim()));
      }
    } else {
      flushTable();
      buffer.push(line);
    }
  }
  flushText();
  flushTable();
  return blocks;
}

function Spans({ spans }: { spans: RichSpan[] }) {
  return (
    <>
      {spans.map((span, j) => (
        <span
          key={j}
          className={`${span.bold ? "font-semibold" : ""} ${span.code ? "font-mono text-[0.92em]" : ""}`}
        >
          {span.text}
        </span>
      ))}
    </>
  );
}

/* Ячейка разбирается тем же парсером: в таблицах агент тоже выделяет жирным. */
const cellSpans = (cell: string) => parseAgentText(cell)[0]?.spans ?? [];

export function AgentText({ text }: { text: string }) {
  return (
    <>
      {splitBlocks(text).map((block, b) =>
        block.kind === "table" ? (
          <div key={b} className="my-2 overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <tbody>
                {block.rows.map((row, r) => (
                  <tr key={r} className={r === 0 ? "bg-gray-50 font-semibold" : "border-t border-gray-100"}>
                    {row.map((cell, c) => (
                      <td
                        key={c}
                        className={`px-2 py-1 align-top ${c > 0 && /^[\d\s.,%−-]+$/.test(cell) ? "text-right tabular-nums" : ""}`}
                      >
                        <Spans spans={cellSpans(cell)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          parseAgentText(block.text).map((line, i) =>
            line.blank ? (
              <span key={`${b}-${i}`} className="block h-2" />
            ) : (
              <span
                key={`${b}-${i}`}
                className={`block ${line.marker ? "pl-4 -indent-4" : ""} ${line.heading ? "mt-1" : ""}`}
              >
                {line.marker && (
                  <span className="inline-block w-4 indent-0 text-gray-500">{line.marker}</span>
                )}
                <Spans spans={line.spans} />
              </span>
            ),
          )
        ),
      )}
    </>
  );
}

/* SQL шага — отдельной строкой: ради него журнал и заводили. */
function stepSql(step: AiAgentStep): string {
  const sql = step.args?.sql ?? step.args?.query;
  return typeof sql === "string" ? sql.trim() : "";
}

function stepArgs(step: AiAgentStep): string {
  return Object.entries(step.args ?? {})
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`)
    .join(", ");
}

function stepTime(step: AiAgentStep): string {
  if (step.elapsed_ms == null) return "";
  return step.elapsed_ms < 1000
    ? `${step.elapsed_ms} мс`
    : `${(step.elapsed_ms / 1000).toFixed(1).replace(".", ",")} с`;
}

/**
 * «Откуда это число» — шаги агента под ответом. Свёрнуто: ответ читают все, а
 * журнал — тот, кто цифре не поверил. Строки — интерполяцией, не разметкой.
 */
export function AgentSteps({ steps }: { steps: AiAgentStep[] }) {
  return (
    <details className="mt-3 border-t border-gray-100 pt-2 text-xs text-gray-600">
      <summary className="cursor-pointer select-none text-gray-500">
        Откуда это число · шагов: {steps.length}
      </summary>
      <ol className="mt-2 list-decimal space-y-2 pl-5">
        {steps.map((s, i) => {
          const sql = stepSql(s);
          const args = stepArgs(s);
          return (
            <li key={i}>
              <div className="flex justify-between gap-2">
                <span>{aiAgentStepLabel(s.tool)}</span>
                {stepTime(s) && (
                  <span className="whitespace-nowrap tabular-nums text-gray-400">{stepTime(s)}</span>
                )}
              </div>
              {sql ? (
                <pre className="mt-1 max-h-44 overflow-auto whitespace-pre-wrap break-all rounded-md bg-gray-50 px-2 py-1.5 font-mono text-[11px] text-gray-700">
                  {sql}
                </pre>
              ) : (
                args && <div className="break-all text-gray-400">{args}</div>
              )}
            </li>
          );
        })}
      </ol>
    </details>
  );
}
